import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { UserConfigContextValue } from "./types";
import { initialUserConfigContextValue, getTableColumnsFromFields } from "./utils";
import { useUsersConfig } from "@hooks/use-config/user";
import { useUserContext } from "@hooks/use-auth-context";

const UserConfigContext = createContext<UserConfigContextValue>(
  initialUserConfigContextValue
);

export const UserConfigContextProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<UserConfigContextValue>(
    initialUserConfigContextValue
  );

  const { getConfig } = useUsersConfig();
  const { user } = useUserContext();
  const hasFetchedConfig = useRef(false);

  useEffect(() => {
    // Don't fetch until we have a valid auth token
    if (!user?.authToken) return;

    const getInitialConfig = async () => {
      const fields = await getConfig();
      if (fields) {
        setState({
          loading: false,
          error: null,
          config: fields,
          tableColumns: getTableColumnsFromFields(fields),
        });
      } else {
        setState({ ...initialUserConfigContextValue, loading: false, config: {} as any });
      }
    };

    if (!hasFetchedConfig.current) {
      hasFetchedConfig.current = true;
      getInitialConfig();
    }
  }, [getConfig, user?.authToken]);

  return (
    <UserConfigContext.Provider value={state}>
      {children}
    </UserConfigContext.Provider>
  );
};

export const useUserConfigContext = () => {
  const context = useContext(UserConfigContext);
  if (!context) {
    throw new Error(
      "useUserConfigContext must be used within a UserConfigContextProvider"
    );
  }
  return context;
};
